<template>
  <div class="projects-page">

    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Projects</h1>
        <p>Manage course projects and practical learning content.</p>
      </div>

      <button
        class="primary-btn"
        @click="openAddModal"
      >
        <i class="bx bx-plus"></i>
        Add Project
      </button>
    </div>

    <!-- Filters -->
    <div class="filter-card">

      <!-- Search -->
      <div class="search-box">
        <i class="bx bx-search"></i>

        <input
          v-model="search"
          type="text"
          placeholder="Search projects..."
        />
      </div>

      <!-- Course -->
      <select v-model="selectedCourse">
        <option value="">
          All Courses
        </option>

        <option
          v-for="course in courses"
          :key="course.course_id"
          :value="course.course_id"
        >
          {{ course.title }}
        </option>
      </select>

      <!-- Category -->
      <!-- <select v-model="selectedCategory">
        <option value="">
          All Categories
        </option>

        <option
          v-for="category in categories"
          :key="category.cat_id"
          :value="category.cat_id"
        >
          {{ category.cat_name }}
        </option>
      </select> -->

      <!-- Creator -->
      <select v-model="selectedCreator">
        <option value="">
          All Creators
        </option>

        <option
          v-for="creator in creators"
          :key="creator.creator_id"
          :value="creator.creator_id"
        >
          {{ creator.cre_username || creator.cre_buss_name || `Creator #${creator.creator_id}` }}
        </option>
      </select>

      <!-- Reset -->
      <button
        class="reset-btn"
        @click="resetFilters"
      >
        <i class="bx bx-reset"></i>
        Reset
      </button>

    </div>

    <!-- Table -->
    <div class="table-card">

      <!-- Table Top -->
      <div class="table-top">
        <div>
          <h3>Project List</h3>

          <span>
            {{ totalProjects }} projects
          </span>
        </div>
      </div>

      <!-- Table -->
      <div class="table-wrapper">

        <table>

          <thead>
            <tr>
              <th>Project</th>
              <th>Course</th>
              <!-- <th>Category</th> -->
              <th>Created By</th>
              <th>Duration</th>
              <th>Video</th>
              <th>Created</th>
              <th class="action-col">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>

            <!-- Project Rows -->
            <tr
              v-for="project in projects"
              :key="project.project_id"
            >

              <!-- Project -->
              <td>
                <div class="project-info">

                  <div class="project-image">

                    <img
                      v-if="project.project_img"
                      :src="project.project_img"
                      :alt="project.project_title"
                    />

                    <i
                      v-else
                      class="bx bx-briefcase"
                    ></i>

                  </div>

                  <div>

                    <strong>
                      {{ project.project_title }}
                    </strong>

                    <small>
                      #{{ project.project_id }}
                    </small>

                  </div>

                </div>
              </td>

              <!-- Course -->
              <td>
                <span class="course-name">
                  {{ getCourseName(project.project_parent) }}
                </span>
              </td>

              <!-- Category -->
              <!-- <td>
                <span class="category-badge">
                  {{ getCategoryName(project.project_cat) }}
                </span>
              </td> -->

              <!-- Creator -->
              <td>
                {{ getCreatorName(project.project_by) }}
              </td>

              <!-- Duration -->
              <td>

                <span
                  v-if="project.project_duration"
                >
                  {{ project.project_duration }}

                  <small class="duration-label">
                    hrs
                  </small>
                </span>

                <span v-else>
                  -
                </span>

              </td>

              <!-- Video -->
              <td>

                <span
                  v-if="project.project_video"
                  class="video-badge"
                >
                  <i class="bx bx-video"></i>
                  Available
                </span>

                <span
                  v-else
                  class="no-video"
                >
                  No video
                </span>

              </td>

              <!-- Created -->
              <td>
                {{ formatDate(project.project_datetime) }}
              </td>

              <!-- Actions -->
              <td>

                <div class="actions">

                  <!-- View -->
                  <button
                    class="icon-btn view"
                    title="View"
                    @click="viewProject(project)"
                  >
                    <i class="bx bx-show"></i>
                  </button>

                  <!-- Edit -->
                  <button
                    class="icon-btn edit"
                    title="Edit"
                    @click="editProject(project)"
                  >
                    <i class="bx bx-edit"></i>
                  </button>

                  <!-- Delete -->
                  <button
                    class="icon-btn delete"
                    title="Delete"
                    @click="confirmDelete(project)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>

                </div>

              </td>

            </tr>

            <!-- Loading -->
            <tr v-if="loading">
              <td colspan="8">

                <div class="empty-state">

                  <i class="bx bx-loader-alt bx-spin"></i>

                  <h4>
                    Loading projects...
                  </h4>

                </div>

              </td>
            </tr>

            <!-- Empty -->
            <tr
              v-if="!loading && projects.length === 0"
            >
              <td colspan="8">

                <div class="empty-state">

                  <i class="bx bx-briefcase"></i>

                  <h4>
                    No projects found
                  </h4>

                  <p>
                    Try changing your search or filters.
                  </p>

                </div>

              </td>
            </tr>

          </tbody>

        </table>

      </div>

      <!-- Pagination -->
      <div
        v-if="totalProjects > 0"
        class="pagination"
      >

        <!-- Showing -->
        <span>
          Showing
          {{ startIndex + 1 }}
          -
          {{ Math.min(endIndex, totalProjects) }}
          of
          {{ totalProjects }}
        </span>

        <div class="pagination-buttons">

          <!-- Previous -->
          <button
            :disabled="currentPage === 1"
            @click="previousPage"
          >
            <i class="bx bx-chevron-left"></i>
          </button>

          <!-- Page Numbers -->
          <button
            v-for="pageNumber in totalPagesCount"
            :key="pageNumber"
            :class="{
              active: currentPage === pageNumber
            }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </button>

          <!-- Next -->
          <button
            :disabled="currentPage === totalPagesCount"
            @click="nextPage"
          >
            <i class="bx bx-chevron-right"></i>
          </button>

        </div>

      </div>

    </div>

    <!-- ===================================================== -->
    <!-- ADD / EDIT MODAL -->
    <!-- ===================================================== -->

    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click.self="closeFormModal"
    >

      <div class="modal">

        <!-- Modal Header -->
        <div class="modal-header">

          <div>

            <h3>
              {{
                isEditing
                  ? 'Edit Project'
                  : 'Add Project'
              }}
            </h3>

            <p>
              {{
                isEditing
                  ? 'Update project information.'
                  : 'Create a new project.'
              }}
            </p>

          </div>

          <button
            class="close-btn"
            @click="closeFormModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Modal Body -->
        <div class="modal-body">

          <div class="form-grid">

            <!-- Course -->
            <div class="form-group">

              <label>
                Course
              </label>

              <select
                v-model="form.project_parent"
              >

                <option value="">
                  Select Course
                </option>

                <option
                  v-for="course in courses"
                  :key="course.course_id"
                  :value="course.course_id"
                >
                  {{ course.title }}
                </option>

              </select>

            </div>

            <!-- Creator -->
            <div class="form-group">

              <label>
                Project By
              </label>

              <select
                v-model="form.project_by"
              >

                <option value="">
                  Select Creator
                </option>

                <option
                  v-for="creator in creators"
                  :key="creator.creator_id"
                  :value="creator.creator_id"
                >
                  {{
                    creator.cre_username ||
                    creator.cre_buss_name ||
                    `Creator #${creator.creator_id}`
                  }}
                </option>

              </select>

            </div>

            <!-- Parent Type -->
            <div class="form-group">

              <label>
                Parent Type
              </label>

              <input
                v-model="form.project_parent_type"
                type="text"
                placeholder="e.g. course"
              />

            </div>

            <!-- Title -->
            <div class="form-group full">

              <label>
                Project Title
                <span>*</span>
              </label>

              <input
                v-model="form.project_title"
                type="text"
                placeholder="Enter project title"
              />

            </div>

            <!-- Meta Title -->
            <div class="form-group">

              <label>
                Meta Title
              </label>

              <input
                v-model="form.project_meta_title"
                type="text"
                placeholder="Enter SEO meta title"
              />

            </div>

            <!-- Duration -->
            <div class="form-group">

              <label>
                Duration (Hours)
              </label>

              <input
                v-model="form.project_duration"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 10"
              />

            </div>

            <!-- Keywords -->
            <div class="form-group full">

              <label>
                Keywords
              </label>

              <input
                v-model="form.project_keywords"
                type="text"
                placeholder="project, practical, development"
              />

            </div>

            <!-- Image -->
            <div class="form-group full">

              <label>
                Project Image URL
              </label>

              <input
                v-model="form.project_img"
                type="text"
                placeholder="Enter project image URL"
              />

            </div>

            <!-- Video -->
            <div class="form-group full">

              <label>
                Project Video URL
              </label>

              <input
                v-model="form.project_video"
                type="text"
                placeholder="Enter project video URL"
              />

            </div>

          </div>

        </div>

        <!-- Modal Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeFormModal"
            :disabled="saving"
          >
            Cancel
          </button>

          <button
            class="primary-btn"
            @click="saveProject"
            :disabled="saving"
          >

            <i
              v-if="!saving"
              class="bx bx-save"
            ></i>

            <i
              v-else
              class="bx bx-loader-alt bx-spin"
            ></i>

            {{
              saving
                ? 'Saving...'
                : (
                    isEditing
                      ? 'Update Project'
                      : 'Create Project'
                  )
            }}

          </button>

        </div>

      </div>

    </div>

    <!-- ===================================================== -->
    <!-- VIEW MODAL -->
    <!-- ===================================================== -->

    <div
      v-if="showViewModal"
      class="modal-overlay"
      @click.self="closeViewModal"
    >

      <div class="modal view-modal">

        <!-- Header -->
        <div class="modal-header">

          <div>

            <h3>
              Project Details
            </h3>

            <p>
              View project information.
            </p>

          </div>

          <button
            class="close-btn"
            @click="closeViewModal"
          >
            <i class="bx bx-x"></i>
          </button>

        </div>

        <!-- Body -->
        <div
          v-if="selectedProject"
          class="modal-body"
        >

          <!-- Project Preview -->
          <div class="project-preview">

            <div class="preview-image">

              <img
                v-if="selectedProject.project_img"
                :src="selectedProject.project_img"
                :alt="selectedProject.project_title"
              />

              <i
                v-else
                class="bx bx-briefcase"
              ></i>

            </div>

            <div>

              <h2>
                {{ selectedProject.project_title }}
              </h2>

              <p>

                {{
                  getCourseName(
                    selectedProject.project_parent
                  )
                }}

                <span>•</span>

                {{
                  getCategoryName(
                    selectedProject.project_cat
                  )
                }}

              </p>

            </div>

          </div>

          <!-- Details -->
          <div class="detail-grid">

            <!-- Project ID -->
            <div class="detail-item">

              <label>
                Project ID
              </label>

              <strong>
                #{{ selectedProject.project_id }}
              </strong>

            </div>

            <!-- Created By -->
            <div class="detail-item">

              <label>
                Created By
              </label>

              <strong>
                {{
                  getCreatorName(
                    selectedProject.project_by
                  )
                }}
              </strong>

            </div>

            <!-- Duration -->
            <div class="detail-item">

              <label>
                Duration
              </label>

              <strong>

                {{
                  selectedProject.project_duration
                    ? selectedProject.project_duration + ' hrs'
                    : '-'
                }}

              </strong>

            </div>

            <!-- Created -->
            <div class="detail-item">

              <label>
                Created
              </label>

              <strong>
                {{
                  formatDate(
                    selectedProject.project_datetime
                  )
                }}
              </strong>

            </div>

            <!-- Parent Type -->
            <div class="detail-item">

              <label>
                Parent Type
              </label>

              <strong>
                {{
                  selectedProject.project_parent_type ||
                  '-'
                }}
              </strong>

            </div>

            <!-- Category -->
            <!-- <div class="detail-item">

              <label>
                Category
              </label>

              <strong>
                {{
                  getCategoryName(
                    selectedProject.project_cat
                  )
                }}
              </strong>

            </div> -->

          </div>

          <!-- Keywords -->
          <div class="keywords-section">

            <label>
              Keywords
            </label>

            <p>
              {{
                selectedProject.project_keywords ||
                '-'
              }}
            </p>

          </div>

          <!-- Video -->
          <div
            v-if="selectedProject.project_video"
            class="video-url"
          >

            <label>
              Video URL
            </label>

            <div>

              <i class="bx bx-link"></i>

              {{ selectedProject.project_video }}

            </div>

          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer">

          <button
            class="secondary-btn"
            @click="closeViewModal"
          >
            Close
          </button>

          <button
            class="primary-btn"
            @click="editProject(selectedProject)"
          >

            <i class="bx bx-edit"></i>

            Edit Project

          </button>

        </div>

      </div>

    </div>

    <!-- ===================================================== -->
    <!-- DELETE MODAL -->
    <!-- ===================================================== -->

    <div
      v-if="showDeleteModal"
      class="modal-overlay"
      @click.self="closeDeleteModal"
    >

      <div class="delete-modal">

        <div class="delete-icon">
          <i class="bx bx-trash"></i>
        </div>

        <h3>
          Delete Project?
        </h3>

        <p>

          Are you sure you want to delete

          <strong>
            {{ selectedProject?.project_title }}
          </strong>?

          This action cannot be undone.

        </p>

        <div class="delete-actions">

          <button
            class="secondary-btn"
            @click="closeDeleteModal"
            :disabled="deleting"
          >
            Cancel
          </button>

          <button
            class="danger-btn"
            @click="deleteProject"
            :disabled="deleting"
          >

            <i
              v-if="!deleting"
              class="bx bx-trash"
            ></i>

            <i
              v-else
              class="bx bx-loader-alt bx-spin"
            ></i>

            {{
              deleting
                ? 'Deleting...'
                : 'Delete Project'
            }}

          </button>

        </div>

      </div>

    </div>

  </div>
</template>
<script>
import {
  getProjectsApi,
  getProjectApi,
  createProjectApi,
  updateProjectApi,
  deleteProjectApi,
} from "@/apis/projectapis";

import { getCoursesApi } from "@/apis/courseapis";

import { getCategoriesApi } from "@/apis/categoryapis";
import { getCreatorsApi } from "@/apis/creatorapis";

export default {
  name: "ProjectsScreen",

  data() {
    return {
      search: "",
      selectedCourse: "",
      selectedCategory: "",
      selectedCreator: "",

      currentPage: 1,
      perPage: 10,

      projects: [],
      courses: [],
      categories: [],
      creators: [],

      totalProjects: 0,
      totalPagesCount: 1,

      loading: false,
      saving: false,
      deleting: false,

      coursesLoading: false,
      categoriesLoading: false,
      creatorsLoading: false,

      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,

      isEditing: false,
      selectedProject: null,

      form: {
        project_parent: "",
        project_parent_type: "",
        project_cat: "",
        project_img: "",
        project_title: "",
        project_meta_title: "",
        project_keywords: "",
        project_duration: "",
        project_video: "",
        project_by: "",
      },
    };
  },

  computed: {
    filteredProjects() {
      return this.projects;
    },

    startIndex() {
      if (!this.totalProjects) {
        return 0;
      }

      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      return this.startIndex + this.projects.length;
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
      this.loadProjects();
    },

    selectedCourse() {
      this.currentPage = 1;
      this.loadProjects();
    },

    selectedCategory() {
      this.currentPage = 1;
      this.loadProjects();
    },

    selectedCreator() {
      this.currentPage = 1;
      this.loadProjects();
    },
  },

  mounted() {
    this.loadProjects();
    this.loadCourses();
    this.loadCategories();
    this.loadCreators();
  },

  methods: {
    // -------------------------------------------------------
    // LOAD PROJECTS
    // -------------------------------------------------------
    async loadProjects() {
      this.loading = true;

      try {
        const response = await getProjectsApi({
          page: this.currentPage,
          limit: this.perPage,
          search: this.search,
          course_id: this.selectedCourse,
          category_id: this.selectedCategory,
          creator_id: this.selectedCreator,
        });

        if (response?.status) {
          this.projects = response.projects || [];

          this.totalProjects = Number(
            response.total || 0
          );

          this.totalPagesCount = Math.max(
            Number(response.totalPages || 1),
            1
          );

          this.currentPage = Math.min(
            Math.max(
              Number(response.page || 1),
              1
            ),
            this.totalPagesCount
          );
        } else {
          this.projects = [];
          this.totalProjects = 0;
          this.totalPagesCount = 1;

          console.error(
            response?.success ||
              "Projects fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "Load Projects Error:",
          error
        );

        this.projects = [];
        this.totalProjects = 0;
        this.totalPagesCount = 1;
      } finally {
        this.loading = false;
      }
    },

    // -------------------------------------------------------
    // LOAD COURSES
    // -------------------------------------------------------
    async loadCourses() {
      this.coursesLoading = true;

      try {
        const response = await getCoursesApi({
          page: 1,
          limit: 100,
          search: "",
          course_typ: "",
          category: "",
          creator_id: "",
        });

        if (response?.status) {
          this.courses = response.courses || [];
        } else {
          this.courses = [];

          console.error(
            response?.success ||
              "Courses fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "Load Courses Error:",
          error
        );

        this.courses = [];
      } finally {
        this.coursesLoading = false;
      }
    },

    // -------------------------------------------------------
    // LOAD CATEGORIES
    // -------------------------------------------------------
    async loadCategories() {
      this.categoriesLoading = true;

      try {
        const response = await getCategoriesApi({
          page: 1,
          limit: 100,
        });

        if (response?.status) {
          this.categories =
            response.categories || [];
        } else {
          this.categories = [];

          console.error(
            response?.success ||
              "Categories fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "Load Categories Error:",
          error
        );

        this.categories = [];
      } finally {
        this.categoriesLoading = false;
      }
    },

    // -------------------------------------------------------
    // LOAD CREATORS
    // -------------------------------------------------------
    async loadCreators() {
      this.creatorsLoading = true;

      try {
        const response = await getCreatorsApi({
          page: 1,
          limit: 100,
        });

        if (response?.status) {
          this.creators =
            response.creators || [];
        } else {
          this.creators = [];

          console.error(
            response?.success ||
              "Creators fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "Load Creators Error:",
          error
        );

        this.creators = [];
      } finally {
        this.creatorsLoading = false;
      }
    },

    // -------------------------------------------------------
    // NAME HELPERS
    // -------------------------------------------------------
    getCourseName(courseId) {
      const course = this.courses.find(
        (item) =>
          Number(item.course_id) ===
          Number(courseId)
      );

      return course
        ? course.title
        : "-";
    },

    getCategoryName(categoryId) {
      const category =
        this.categories.find(
          (item) =>
            Number(item.cat_id) ===
            Number(categoryId)
        );

      return category
        ? category.cat_name
        : "-";
    },

    getCreatorName(creatorId) {
      const creator =
        this.creators.find(
          (item) =>
            Number(item.creator_id) ===
            Number(creatorId)
        );

      return creator
        ? creator.cre_username
        : "-";
    },

    // -------------------------------------------------------
    // DATE
    // -------------------------------------------------------
    formatDate(date) {
      if (!date) {
        return "-";
      }

      const value = new Date(date);

      if (Number.isNaN(value.getTime())) {
        return date;
      }

      return value.toLocaleDateString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );
    },

    // -------------------------------------------------------
    // FILTER RESET
    // -------------------------------------------------------
    resetFilters() {
      this.search = "";
      this.selectedCourse = "";
      this.selectedCategory = "";
      this.selectedCreator = "";
      this.currentPage = 1;

      this.loadProjects();
    },

    // -------------------------------------------------------
    // FORM RESET
    // -------------------------------------------------------
    resetForm() {
      this.form = {
        project_parent: "",
        project_parent_type: "",
        project_cat: "",
        project_img: "",
        project_title: "",
        project_meta_title: "",
        project_keywords: "",
        project_duration: "",
        project_video: "",
        project_by: "",
      };
    },

    // -------------------------------------------------------
    // ADD
    // -------------------------------------------------------
    openAddModal() {
      this.isEditing = false;
      this.selectedProject = null;

      this.resetForm();

      this.showFormModal = true;
    },

    // -------------------------------------------------------
    // EDIT
    // -------------------------------------------------------
    editProject(project) {
      if (!project) {
        return;
      }

      this.isEditing = true;
      this.selectedProject = project;

      this.form = {
        project_id:
          project.project_id,

        project_parent:
          project.project_parent || "",

        project_parent_type:
          project.project_parent_type || "",

        project_cat:
          project.project_cat || "",

        project_img:
          project.project_img || "",

        project_title:
          project.project_title || "",

        project_meta_title:
          project.project_meta_title || "",

        project_keywords:
          project.project_keywords || "",

        project_duration:
          project.project_duration ?? "",

        project_video:
          project.project_video || "",

        project_by:
          project.project_by || "",
      };

      this.showViewModal = false;
      this.showFormModal = true;
    },

    // -------------------------------------------------------
    // CLOSE FORM
    // -------------------------------------------------------
    closeFormModal() {
      this.showFormModal = false;

      this.resetForm();

      this.isEditing = false;
    },

    // -------------------------------------------------------
    // SAVE
    // -------------------------------------------------------
    async saveProject() {
      const title = String(
        this.form.project_title || ""
      ).trim();

      if (!title) {
        alert(
          "Please enter project title."
        );
        return;
      }

      if (!this.form.project_parent) {
        alert(
          "Please select a course."
        );
        return;
      }

      this.saving = true;

      try {
        let response;

        if (this.isEditing) {
          response =
            await updateProjectApi(
              this.form
            );
        } else {
          response =
            await createProjectApi(
              this.form
            );
        }

        if (response?.status) {
          this.closeFormModal();

          await this.loadProjects();

          alert(
            response.success ||
              (this.isEditing
                ? "Project updated successfully."
                : "Project created successfully.")
          );
        } else {
          alert(
            response?.success ||
              "Project save failed."
          );
        }
      } catch (error) {
        console.error(
          "Save Project Error:",
          error
        );

        alert(
          "Unable to save project."
        );
      } finally {
        this.saving = false;
      }
    },

    // -------------------------------------------------------
    // VIEW
    // -------------------------------------------------------
    async viewProject(project) {
      if (!project?.project_id) {
        return;
      }

      try {
        const response =
          await getProjectApi(
            project.project_id
          );

        if (response?.status) {
          this.selectedProject =
            response.project;

          this.showViewModal = true;
        } else {
          alert(
            response?.success ||
              "Project fetch failed."
          );
        }
      } catch (error) {
        console.error(
          "View Project Error:",
          error
        );

        alert(
          "Unable to load project details."
        );
      }
    },

    // -------------------------------------------------------
    // DELETE CONFIRM
    // -------------------------------------------------------
    confirmDelete(project) {
      if (!project?.project_id) {
        return;
      }

      this.selectedProject = project;
      this.showDeleteModal = true;
    },

    // -------------------------------------------------------
    // DELETE
    // -------------------------------------------------------
    async deleteProject() {
      if (
        !this.selectedProject?.project_id
      ) {
        return;
      }

      this.deleting = true;

      try {
        const response =
          await deleteProjectApi(
            this.selectedProject.project_id
          );

        if (response?.status) {
          this.showDeleteModal = false;

          this.selectedProject = null;

          // If last record of current page
          // was deleted, move back one page.
          if (
            this.projects.length === 1 &&
            this.currentPage > 1
          ) {
            this.currentPage--;
          }

          await this.loadProjects();

          alert(
            response.success ||
              "Project deleted successfully."
          );
        } else {
          alert(
            response?.success ||
              "Project deletion failed."
          );
        }
      } catch (error) {
        console.error(
          "Delete Project Error:",
          error
        );

        alert(
          "Unable to delete project."
        );
      } finally {
        this.deleting = false;
      }
    },

    // -------------------------------------------------------
    // PAGINATION
    // -------------------------------------------------------
    goToPage(page) {
      if (
        page < 1 ||
        page > this.totalPagesCount ||
        page === this.currentPage
      ) {
        return;
      }

      this.currentPage = page;

      this.loadProjects();
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;

        this.loadProjects();
      }
    },

    nextPage() {
      if (
        this.currentPage <
        this.totalPagesCount
      ) {
        this.currentPage++;

        this.loadProjects();
      }
    },

    // -------------------------------------------------------
    // CLOSE VIEW
    // -------------------------------------------------------
    closeViewModal() {
      this.showViewModal = false;
      this.selectedProject = null;
    },

    // -------------------------------------------------------
    // CLOSE DELETE
    // -------------------------------------------------------
    closeDeleteModal() {
      this.showDeleteModal = false;
      this.selectedProject = null;
    },
  },
};
</script>

<style scoped>
.projects-page {
  min-height: 100%;
  background: #f8fafc;
  padding: 24px;
  color: #0f172a;
}

/* Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.page-header h1 {
  margin: 0 0 5px;
  font-size: 24px;
  font-weight: 700;
}

.page-header p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

/* Buttons */
.primary-btn,
.secondary-btn,
.danger-btn,
.reset-btn {
  border: 0;
  border-radius: 9px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: 0.2s;
}

.primary-btn {
  background: #2563eb;
  color: #fff;
}

.primary-btn:hover {
  background: #1d4ed8;
}

.secondary-btn {
  background: #f1f5f9;
  color: #334155;
}

.secondary-btn:hover {
  background: #e2e8f0;
}

.danger-btn {
  background: #dc2626;
  color: #fff;
}

.danger-btn:hover {
  background: #b91c1c;
}

/* Filters */
.filter-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 15px;
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 18px;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 240px;
}

.search-box i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
}

.search-box input,
.filter-card select {
  height: 42px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  color: #334155;
  font-size: 14px;
  outline: none;
}

.search-box input {
  width: 100%;
  padding: 0 12px 0 40px;
}

.filter-card select {
  min-width: 160px;
  padding: 0 35px 0 12px;
}

.search-box input:focus,
.filter-card select:focus,
.form-group input:focus,
.form-group select:focus {
  border-color: #2563eb;
}

.reset-btn {
  height: 42px;
  background: #f1f5f9;
  color: #475569;
}

/* Table */
.table-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.table-top {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.table-top h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.table-top span {
  font-size: 13px;
  color: #64748b;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
}

th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 13px 16px;
  text-align: left;
  white-space: nowrap;
}

td {
  padding: 15px 16px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
}

tr:hover td {
  background: #fafcff;
}

.action-col {
  text-align: right;
}

.project-info {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 250px;
}

.project-image {
  width: 44px;
  height: 44px;
  border-radius: 9px;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  flex-shrink: 0;
}

.project-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-info strong {
  display: block;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-info small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 11px;
}

.course-name {
  color: #334155;
  font-weight: 500;
}

.category-badge {
  display: inline-flex;
  padding: 5px 9px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
}

.duration-label {
  color: #94a3b8;
  font-size: 11px;
}

.video-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #16a34a;
  font-size: 12px;
  font-weight: 600;
}

.no-video {
  color: #94a3b8;
  font-size: 12px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.icon-btn {
  width: 34px;
  height: 34px;
  border-radius: 7px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
}

.icon-btn.view {
  color: #475569;
}

.icon-btn.edit {
  color: #2563eb;
}

.icon-btn.delete {
  color: #dc2626;
}

.icon-btn:hover {
  background: #f8fafc;
}

/* Empty */
.empty-state {
  padding: 55px 20px;
  text-align: center;
}

.empty-state i {
  font-size: 42px;
  color: #cbd5e1;
}

.empty-state h4 {
  margin: 10px 0 5px;
  color: #334155;
}

.empty-state p {
  margin: 0;
  color: #94a3b8;
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 13px;
}

.pagination-buttons {
  display: flex;
  gap: 5px;
}

.pagination-buttons button {
  min-width: 34px;
  height: 34px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  border-radius: 7px;
  cursor: pointer;
}

.pagination-buttons button.active {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.pagination-buttons button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal {
  width: 760px;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 20px 22px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.modal-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
}

.modal-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: #f1f5f9;
  color: #475569;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 22px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 17px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.form-group label span {
  color: #dc2626;
}

.form-group input,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 11px 12px;
  color: #334155;
  font-size: 14px;
  outline: none;
  background: #fff;
}

.modal-footer {
  padding: 16px 22px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* View */
.project-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-bottom: 22px;
  border-bottom: 1px solid #e2e8f0;
}

.preview-image {
  width: 70px;
  height: 70px;
  border-radius: 12px;
  overflow: hidden;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  flex-shrink: 0;
}

.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-preview h2 {
  margin: 0 0 5px;
  font-size: 19px;
}

.project-preview p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}

.project-preview p span {
  margin: 0 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 22px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 9px;
}

.detail-item label {
  display: block;
  color: #64748b;
  font-size: 11px;
  margin-bottom: 5px;
}

.detail-item strong {
  color: #0f172a;
  font-size: 13px;
}

.keywords-section {
  margin-top: 20px;
}

.keywords-section label {
  display: block;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 7px;
}

.keywords-section p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.video-url {
  margin-top: 20px;
}

.video-url label {
  display: block;
  margin-bottom: 8px;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
}

.video-url div {
  padding: 11px 13px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #2563eb;
  font-size: 13px;
  word-break: break-all;
}

/* Delete */
.delete-modal {
  width: 420px;
  max-width: 100%;
  background: #fff;
  border-radius: 14px;
  padding: 30px;
  text-align: center;
}

.delete-icon {
  width: 54px;
  height: 54px;
  margin: 0 auto 15px;
  border-radius: 50%;
  background: #fef2f2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
}

.delete-modal h3 {
  margin: 0 0 8px;
  font-size: 18px;
}

.delete-modal p {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
}

.delete-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
}

/* Responsive */
@media (max-width: 1000px) {
  .filter-card {
    flex-wrap: wrap;
  }

  .search-box {
    flex-basis: 100%;
  }

  .filter-card select {
    flex: 1;
  }
}

@media (max-width: 650px) {
  .projects-page {
    padding: 16px;
  }

  .page-header {
    align-items: flex-start;
  }

  .page-header h1 {
    font-size: 21px;
  }

  .page-header .primary-btn {
    padding: 9px 12px;
  }

  .page-header .primary-btn i {
    display: none;
  }

  .filter-card {
    display: block;
  }

  .search-box {
    margin-bottom: 10px;
  }

  .filter-card select {
    width: 100%;
    margin-bottom: 10px;
  }

  .reset-btn {
    width: 100%;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full {
    grid-column: auto;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>